import {
  blue,
  cyan,
  gold,
  green,
  magenta,
  orange,
  purple,
  red,
  volcano,
} from "@ant-design/colors";
import {
  CloudServerOutlined,
  DesktopOutlined,
  FileProtectOutlined,
  ForwardOutlined,
  KeyOutlined,
  SolutionOutlined,
  TagOutlined,
  UnlockOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  ProviderNodeTitles,
  SharedNodeTitles,
  identityTypeToLabel,
} from "@p0security/shared/assessment/constants";
import { isConnectedNode } from "@p0security/shared/graph/types";
import { AssessmentNodes } from "@p0security/shared/types/assessment/data/index";
import { widetype } from "@p0security/util/collections";
import { DiscoverFlowRenderer } from "components/GraphTable/DiscoverVisualization";
import {
  DiscoverLinearCollapse,
  DiscoverRenderArgs,
} from "components/GraphTable/DiscoverVisualization/types";
import { compact } from "lodash";
import pluralize from "pluralize";

import { toConsumerText } from "../../cells/ConsumersList";
import { CredentialDisplay, credentialTitle } from "../../cells/Credential";
import { IdentityCell } from "../../cells/Identity";
import { CellInner } from "../../cells/Inner";
import { Lateral } from "../../cells/Lateral";
import { PermissionSetLabel } from "../../cells/PermissionSet";
import { PrivilegeLink } from "../../cells/PrivilegeLink";
import { Resource } from "../../cells/Resource";
import { HydratedRiskLink } from "../../cells/RiskLink";
import { NodeText } from "../NodeText";
import { resourceLabels } from "../resource/labels";

/** Collapses lateral->identity->entitlement linear paths in DiscoverVisualization */
export const AssessmentCollapse: DiscoverLinearCollapse<AssessmentNodes> = {
  predicate: isConnectedNode("lateral"),
  length: 3,
  title: "Lateral movement to entitlement",
};

export const AssessmentNodeRender: DiscoverFlowRenderer<
  AssessmentNodes,
  Pick<DiscoverRenderArgs, "provider">
> = {
  condition: {
    content: (node) => <label htmlFor="text">{node.data?.title}</label>,
    icon: <TagOutlined />,
    size: 1,
    style: () => ({ backgroundColor: magenta[0], borderColor: magenta[1] }),
    title: () => SharedNodeTitles.condition,
  },
  consumer: {
    content: (node) => toConsumerText(node.data),
    icon: <DesktopOutlined />,
    size: 1,
    style: () => ({ backgroundColor: purple[0], borderColor: purple[1] }),
    title: () => SharedNodeTitles.consumer,
  },
  credential: {
    content: (node) => (
      <CredentialDisplay credential={node.data} id={node.key} />
    ),
    icon: <KeyOutlined />,
    size: 1,
    style: () => ({ backgroundColor: volcano[1], borderColor: volcano[2] }),
    title: credentialTitle,
  },
  grant: {
    content: (node) =>
      NodeText(undefined, node.data.provider, { noHover: true }).grant?.(
        node
      ) ?? "",
    icon: <SolutionOutlined />,
    size: 2,
    style: () => ({ backgroundColor: blue[0], borderColor: blue[1] }),
    title: ({ data }) => ProviderNodeTitles[data.provider].grant,
  },
  identity: {
    content: (node) => <IdentityCell identity={node.data} />,
    icon: <UserOutlined />,
    size: 1,
    style: () => ({ backgroundColor: green[1], borderColor: green[2] }),
    title: ({ data }) => identityTypeToLabel(data),
  },
  lateral: {
    content: (node) => <Lateral node={node} />,
    icon: <ForwardOutlined />,
    size: 2,
    style: () => ({ backgroundColor: cyan[1], borderColor: cyan[2] }),
    title: () => SharedNodeTitles.lateral,
  },
  permissionSet: {
    content: ({ data }) => <PermissionSetLabel label={data.label} />,
    icon: <FileProtectOutlined />,
    size: 1,
    style: () => ({ backgroundColor: purple[0], borderColor: purple[1] }),
    title: (_, { provider }) =>
      widetype.get(ProviderNodeTitles[provider], "permissionSet") ?? "",
  },
  privilege: {
    content: (node, { provider }) => (
      <PrivilegeLink privilege={node.key} provider={provider} />
    ),
    icon: <UnlockOutlined />,
    size: 1,
    style: () => ({ backgroundColor: orange[1], borderColor: orange[2] }),
    title: (_, { provider }) =>
      pluralize.singular(ProviderNodeTitles[provider].privileges),
  },
  privilegeSet: {
    content: ({ key }) => <CellInner>{key}</CellInner>,
    icon: <FileProtectOutlined />,
    size: 1,
    style: () => ({ backgroundColor: purple[0], borderColor: purple[1] }),
    title: (_, { provider }) => ProviderNodeTitles[provider].privilegeSet,
  },
  resource: {
    content: (node) => <Resource resource={node} shortPath />,
    icon: <CloudServerOutlined />,
    size: 1,
    style: () => ({ backgroundColor: gold[2], borderColor: gold[3] }),
    title: ({ data }, { provider }) => {
      const { service, type } = resourceLabels(data, provider, true);
      return compact([service, type]).join(" ");
    },
  },
  risk: {
    content: (node) => <HydratedRiskLink id={node.key} />,
    icon: <WarningOutlined />,
    size: 1,
    style: () => ({ backgroundColor: red[1], borderColor: red[2] }),
    title: () => SharedNodeTitles.risk,
  },
  usage: {
    content: (node, { provider }) =>
      NodeText(undefined, provider, { noHover: true }).usage?.(node) ?? "",
    icon: <UnlockOutlined />,
    size: 1,
    style: () => ({ backgroundColor: orange[1], borderColor: orange[2] }),
    title: (_, { provider }) => ProviderNodeTitles[provider].privileges,
  },
};
